import { createClient } from '@connectrpc/connect';
import { FamilyService, ParameterSource, QuickFillSnapshotShape } from './gen/family_service_pb';
import {
  clearPendingRevitOperation,
  getOperationProgressGrpc,
  pollRevitOperation,
  setPendingRevitOperation,
  type PendingRevitOperationRecoveryMetadata,
  type RevitOperationProgressSnapshot,
} from './operationProgressClient';
import { getGrpcTransport } from './transport';
import { useIntegrationDiagnosticsStore } from '../../stores/useIntegrationDiagnosticsStore';
import {
  recordConnectorLiveEvidenceDispatch,
  recordConnectorLiveEvidenceDispatchOperationId,
} from '../../config/connectorLiveEvidence';
import { webLog } from './webLogger';
import { mapFamilyUndoEntries, mapProjectUndoEntries } from './parameterUndoMappers';
import type { ConnectorParameterUndoEntry, ConnectorProjectParameterUndoEntry } from '../../types/parameterSetupUndo';
import { RevitOperationAcceptanceTimeoutMs } from '../../lib/revit/revitOperationTimeoutPolicy';
import { normalizeParameterSource } from '../../lib/revit/parameterSource';

interface GrpcFamilyParameterValue {
  parameterId: string;
  parameterName: string;
  value: string;
  isReadOnly: boolean;
  isInstance: boolean;
  dataType: string;
}

interface GrpcFamilyInfo {
  id: string;
  name: string;
  category: string;
  isEditable: boolean;
  isSystemFamily: boolean;
  types: Array<{
    id: string;
    name: string;
    parameterValues: GrpcFamilyParameterValue[];
  }>;
}

interface GrpcAvailableParameter {
  name: string;
  label: string;
  parameterType: string;
  group: string;
  isInstance: boolean;
  dataType: string;
  isProjectBinding: boolean;
  categories: string[];
  // Absent on hosts built before the source field existed.
  source?: ParameterSource;
}

interface GrpcPipeElement {
  id: string;
  familyName: string;
  typeName: string;
  typeId: string;
  category: string;
  elementName: string;
  parameterValues: GrpcFamilyParameterValue[];
}

interface GetFamiliesResult {
  success: boolean;
  families?: GrpcFamilyInfo[];
  error?: string;
}

interface QuickFillDataResult extends GetFamiliesResult {
  availableParameters?: GrpcAvailableParameter[];
  pipeElements?: GrpcPipeElement[];
}

function getClient() {
  const transport = getGrpcTransport();
  if (!transport) {
    return null;
  }

  return createClient(FamilyService, transport);
}

export async function getFamiliesWithParametersGrpc(
  parameterNames?: string[],
  options?: {
    onProgress?: (progress: RevitOperationProgressSnapshot) => void;
    scopedFamilyIds?: string[];
  }
): Promise<GetFamiliesResult> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('GetFamiliesWithParameters: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    if (options?.onProgress) {
      const startResponse = await client.startGetFamiliesWithParameters({
        parameterNames: parameterNames ?? [],
        scopedFamilyIds: options.scopedFamilyIds ?? [],
      });
      if (startResponse.isSuccess && startResponse.operationId) {
        const operationResult = await pollRevitOperation(startResponse.operationId, {
          maxRevitWaitMs: RevitOperationAcceptanceTimeoutMs,
          onProgress: options.onProgress,
          parseResult: parseGetFamiliesWithParametersResultJson,
        });
        if (!operationResult.result) {
          const error = operationResult.error || 'GetFamiliesWithParameters operation failed';
          useIntegrationDiagnosticsStore.getState().setLastGrpcError(`GetFamiliesWithParameters: ${error}`);
          return { success: false, error };
        }

        return operationResult.result;
      }

      return { success: false, error: startResponse.error || 'GetFamiliesWithParameters did not start a progress operation' };
    }

    const response = await client.getFamiliesWithParameters({
      parameterNames: parameterNames ?? [],
      scopedFamilyIds: options?.scopedFamilyIds ?? [],
    });
    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (response.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`GetFamiliesWithParameters: ${response.error}`);
    }
    return mapGetFamiliesWithParametersResponse(response);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'GetFamiliesWithParameters gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`GetFamiliesWithParameters: ${message}`);
    return { success: false, error: message };
  }
}

export type QuickFillSnapshotShapeOption = 'familyType' | 'instanceOnly';

export interface QuickFillDataScope {
  familyIds?: string[];
  familyNames?: string[];
  familyTypeKeys?: string[];
  elementIds?: string[];
  categoryNames?: string[];
}

const mapQuickFillSnapshotShape = (
  snapshotShape?: QuickFillSnapshotShapeOption
): QuickFillSnapshotShape =>
  snapshotShape === 'instanceOnly'
    ? QuickFillSnapshotShape.INSTANCE_ONLY
    : QuickFillSnapshotShape.FAMILY_TYPE;

const normalizeQuickFillScopeValues = (values?: string[]): string[] =>
  Array.from(new Set((Array.isArray(values) ? values : []).map((value) => value.trim()).filter(Boolean)));

const buildQuickFillDataRequest = (
  parameterNames: string[] | undefined,
  snapshotShape: QuickFillSnapshotShape,
  dataScope?: QuickFillDataScope
) => ({
  parameterNames: parameterNames ?? [],
  snapshotShape,
  scopedFamilyIds: normalizeQuickFillScopeValues(dataScope?.familyIds),
  scopedFamilyNames: normalizeQuickFillScopeValues(dataScope?.familyNames),
  scopedFamilyTypeKeys: normalizeQuickFillScopeValues(dataScope?.familyTypeKeys),
  scopedElementIds: normalizeQuickFillScopeValues(dataScope?.elementIds),
  scopedCategoryNames: normalizeQuickFillScopeValues(dataScope?.categoryNames),
});

function parseGetFamiliesWithParametersResultJson(resultJson: string): GetFamiliesResult {
  return mapGetFamiliesWithParametersResponse(JSON.parse(resultJson));
}

function mapGetFamiliesWithParametersResponse(response: any): GetFamiliesResult {
  return {
    success: Boolean(response.isSuccess),
    families: Array.isArray(response.families)
      ? response.families.map((f: any) => ({
          id: f.id,
          name: f.name,
          category: f.category,
          isEditable: Boolean(f.isEditable),
          isSystemFamily: Boolean(f.isSystemFamily),
          types: Array.isArray(f.types)
            ? f.types.map((t: any) => ({
                id: t.id,
                name: t.name,
                parameterValues: Array.isArray(t.parameterValues)
                  ? t.parameterValues.map((pv: any) => ({
                      parameterId: pv.parameterId,
                      parameterName: pv.parameterName,
                      value: pv.value,
                      isReadOnly: Boolean(pv.isReadOnly),
                      isInstance: Boolean(pv.isInstance),
                      dataType: typeof pv.dataType === 'string' ? pv.dataType : '',
                    }))
                  : [],
              }))
            : [],
        }))
      : undefined,
    error: response.error || undefined,
  };
}

export async function getAvailableParametersGrpc(
  options?: { onProgress?: (progress: RevitOperationProgressSnapshot) => void }
): Promise<{
  success: boolean;
  parameters?: GrpcAvailableParameter[];
  error?: string;
}> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('GetAvailableParameters: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    if (options?.onProgress) {
      const startResponse = await client.startGetAvailableParameters({});
      if (startResponse.isSuccess && startResponse.operationId) {
        const operationResult = await pollRevitOperation(startResponse.operationId, {
          onProgress: options.onProgress,
          parseResult: parseAvailableParametersResultJson,
        });
        if (!operationResult.result) {
          const error = operationResult.error || 'GetAvailableParameters operation failed';
          useIntegrationDiagnosticsStore.getState().setLastGrpcError(`GetAvailableParameters: ${error}`);
          return { success: false, error };
        }

        return operationResult.result;
      }

      return { success: false, error: startResponse.error || 'GetAvailableParameters did not start a progress operation' };
    }

    const response = await client.getAvailableParameters({});
    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (response.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`GetAvailableParameters: ${response.error}`);
    }
    return mapAvailableParametersResponse(response);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'GetAvailableParameters gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`GetAvailableParameters: ${message}`);
    return { success: false, error: message };
  }
}

function parseAvailableParametersResultJson(resultJson: string): {
  success: boolean;
  parameters?: GrpcAvailableParameter[];
  error?: string;
} {
  return mapAvailableParametersResponse(JSON.parse(resultJson));
}

function mapAvailableParametersResponse(response: any): {
  success: boolean;
  parameters?: GrpcAvailableParameter[];
  error?: string;
} {
  return {
    success: Boolean(response.isSuccess),
    parameters: Array.isArray(response.parameters)
      ? response.parameters.map((p: any) => ({
          name: p.name,
          label: p.label,
          parameterType: p.parameterType,
          group: p.group,
          isInstance: Boolean(p.isInstance),
          // Absent on hosts built before the data_type field existed; '' means "unknown".
          dataType: p.dataType ?? '',
          isProjectBinding: Boolean(p.isProjectBinding),
          categories: Array.isArray(p.categories)
            ? p.categories.filter((category: unknown): category is string => typeof category === 'string')
            : [],
          // Tolerates both numeric enum values (connect client) and proto-JSON
          // name strings (polled-operation payloads from the plugin).
          source: normalizeParameterSource(p.source) ?? undefined,
        }))
      : undefined,
    error: response.error || undefined,
  };
}

export async function getQuickFillDataGrpc(
  parameterNames?: string[],
  options?: {
    onProgress?: (progress: RevitOperationProgressSnapshot) => void;
    snapshotShape?: QuickFillSnapshotShapeOption;
    dataScope?: QuickFillDataScope;
  }
): Promise<QuickFillDataResult> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('GetQuickFillData: gRPC transport is unavailable');
    webLog.error('familyClient', 'getQuickFillData: transport unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    const snapshotShape = mapQuickFillSnapshotShape(options?.snapshotShape);
    const request = buildQuickFillDataRequest(parameterNames, snapshotShape, options?.dataScope);
    webLog.info(
      'familyClient',
      `getQuickFillData: request-sent shape=${options?.snapshotShape ?? 'familyType'} paramCount=${parameterNames?.length ?? 0} scopeFamilies=${request.scopedFamilyIds.length + request.scopedFamilyNames.length} scopeTypes=${request.scopedFamilyTypeKeys.length} scopeElements=${request.scopedElementIds.length} scopeCategories=${request.scopedCategoryNames.length} params=${(parameterNames ?? []).slice(0, 12).join(',')}`
    );
    try {
      const startResponse = await client.startQuickFillLoad(request);
      if (startResponse.isSuccess && startResponse.operationId) {
        const operationResult = await pollRevitOperation(startResponse.operationId, {
          onProgress: options?.onProgress,
          parseResult: parseQuickFillDataResultJson,
        });
        if (!operationResult.success) {
          useIntegrationDiagnosticsStore.getState().setLastGrpcError(`GetQuickFillData: ${operationResult.error || 'operation failed'}`);
          return { success: false, error: operationResult.error || 'Quick Fill operation failed' };
        }

        if (operationResult.result?.success) {
          useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
        }
        return operationResult.result ?? { success: false, error: 'Quick Fill operation returned no result' };
      }

      webLog.warn('familyClient', `getQuickFillData: async start failed ${startResponse.error || ''}; falling back to unary`);
    } catch (startError) {
      const message = startError instanceof Error ? startError.message : 'startQuickFillLoad gRPC error';
      webLog.warn('familyClient', `getQuickFillData: async start unavailable (${message}); falling back to unary`);
    }

    const response = await client.getQuickFillData(request);
    webLog.info(
      'familyClient',
      `getQuickFillData: response success=${response.isSuccess} families=${response.families?.length ?? 0} elements=${response.pipeElements?.length ?? 0} availableParameters=${response.availableParameters?.length ?? 0} error=${response.error || ''}`
    );
    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (response.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`GetQuickFillData: ${response.error}`);
    }
    return {
      success: response.isSuccess,
      families: response.families,
      availableParameters: response.availableParameters,
      pipeElements: response.pipeElements,
      error: response.error || undefined,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'GetQuickFillData gRPC error';
    webLog.error('familyClient', `getQuickFillData: failed ${message}`);
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`GetQuickFillData: ${message}`);
    return { success: false, error: message };
  }
}

function parseQuickFillDataResultJson(resultJson: string): QuickFillDataResult {
  const parsed = JSON.parse(resultJson);
  return {
    success: Boolean(parsed.isSuccess),
    families: parsed.families ?? [],
    availableParameters: parsed.availableParameters ?? [],
    pipeElements: parsed.pipeElements ?? [],
    error: parsed.error || undefined,
  };
}

export async function updateFamilyTypeParameterGrpc(
  familyTypeName: string,
  parameterName: string,
  value: string
): Promise<{
  success: boolean;
  familyType?: string;
  parameter?: string;
  newValue?: string;
  error?: string;
}> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('UpdateFamilyTypeParameter: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    const response = await client.updateFamilyTypeParameter({
      familyTypeName,
      parameterName,
      value,
    });
    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (response.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`UpdateFamilyTypeParameter: ${response.error}`);
    }
    return {
      success: response.isSuccess,
      familyType: response.familyType || undefined,
      parameter: response.parameter || undefined,
      newValue: response.newValue || undefined,
      error: response.error || undefined,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'UpdateFamilyTypeParameter gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`UpdateFamilyTypeParameter: ${message}`);
    return { success: false, error: message };
  }
}

export async function updateFamilyTypeParametersGrpc(
  updates: Array<{
    familyType: string;
    parameterName: string;
    value: string;
    familyTypeId?: string;
    targetElementIds?: string[];
  }>,
  options?: { onProgress?: (progress: RevitOperationProgressSnapshot) => void }
): Promise<{
  success: boolean;
  totalUpdates?: number;
  successCount?: number;
  errorCount?: number;
  errors?: string[];
  error?: string;
}> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('UpdateFamilyTypeParameters: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    if (options?.onProgress) {
      try {
        const startResponse = await client.startUpdateFamilyTypeParameters({ updates });
        if (startResponse.isSuccess && startResponse.operationId) {
          const operationResult = await pollRevitOperation(startResponse.operationId, {
            onProgress: options.onProgress,
            parseResult: parseBatchUpdateResultJson,
          });
          if (!operationResult.success || !operationResult.result) {
            const error = operationResult.error || 'UpdateFamilyTypeParameters operation failed';
            useIntegrationDiagnosticsStore.getState().setLastGrpcError(`UpdateFamilyTypeParameters: ${error}`);
            return { success: false, error };
          }

          if (operationResult.result.success) {
            useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
          } else if (operationResult.result.error) {
            useIntegrationDiagnosticsStore
              .getState()
              .setLastGrpcError(`UpdateFamilyTypeParameters: ${operationResult.result.error}`);
          }
          return operationResult.result;
        }
      } catch (startError) {
        const message = startError instanceof Error ? startError.message : 'StartUpdateFamilyTypeParameters gRPC error';
        webLog.warn('familyClient', `updateFamilyTypeParameters: async start unavailable (${message}); falling back to unary`);
      }
    }

    const response = await client.updateFamilyTypeParameters({ updates });
    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (response.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`UpdateFamilyTypeParameters: ${response.error}`);
    }
    return {
      success: response.isSuccess,
      totalUpdates: response.totalUpdates || undefined,
      successCount: response.successCount || undefined,
      errorCount: response.errorCount || undefined,
      errors: response.errors.length > 0 ? response.errors : undefined,
      error: response.error || undefined,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'UpdateFamilyTypeParameters gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`UpdateFamilyTypeParameters: ${message}`);
    return { success: false, error: message };
  }
}

export async function updateElementParametersGrpc(
  updates: Array<{ elementId: string; parameterName: string; value: string }>,
  options?: { onProgress?: (progress: RevitOperationProgressSnapshot) => void }
): Promise<{
  success: boolean;
  totalUpdates?: number;
  successCount?: number;
  errorCount?: number;
  errors?: string[];
  error?: string;
}> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('UpdateElementParameters: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    if (options?.onProgress) {
      try {
        const startResponse = await client.startUpdateElementParameters({ updates });
        if (startResponse.isSuccess && startResponse.operationId) {
          const operationResult = await pollRevitOperation(startResponse.operationId, {
            onProgress: options.onProgress,
            parseResult: parseBatchUpdateResultJson,
          });
          if (!operationResult.success || !operationResult.result) {
            const error = operationResult.error || 'UpdateElementParameters operation failed';
            useIntegrationDiagnosticsStore.getState().setLastGrpcError(`UpdateElementParameters: ${error}`);
            return { success: false, error };
          }

          if (operationResult.result.success) {
            useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
          } else if (operationResult.result.error) {
            useIntegrationDiagnosticsStore
              .getState()
              .setLastGrpcError(`UpdateElementParameters: ${operationResult.result.error}`);
          }
          return operationResult.result;
        }
      } catch (startError) {
        const message = startError instanceof Error ? startError.message : 'StartUpdateElementParameters gRPC error';
        webLog.warn('familyClient', `updateElementParameters: async start unavailable (${message}); falling back to unary`);
      }
    }

    const response = await client.updateElementParameters({ updates });
    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (response.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`UpdateElementParameters: ${response.error}`);
    }
    return {
      success: response.isSuccess,
      totalUpdates: response.totalUpdates || undefined,
      successCount: response.successCount || undefined,
      errorCount: response.errorCount || undefined,
      errors: response.errors.length > 0 ? response.errors : undefined,
      error: response.error || undefined,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'UpdateElementParameters gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`UpdateElementParameters: ${message}`);
    return { success: false, error: message };
  }
}

export async function startQuickFillApplyGrpc(
  familyTypeUpdates: Array<{
    familyType: string;
    parameterName: string;
    value: string;
    familyTypeId?: string;
    targetElementIds?: string[];
  }>,
  elementUpdates: Array<{ elementId: string; parameterName: string; value: string }>,
  options?: { onProgress?: (progress: RevitOperationProgressSnapshot) => void }
): Promise<{
  success: boolean;
  familyTypeResult?: {
    success: boolean;
    totalUpdates?: number;
    successCount?: number;
    errorCount?: number;
    errors?: string[];
    error?: string;
  };
  elementResult?: {
    success: boolean;
    totalUpdates?: number;
    successCount?: number;
    errorCount?: number;
    errors?: string[];
    error?: string;
  };
  error?: string;
}> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('StartQuickFillApply: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    const startResponse = await client.startQuickFillApply({
      familyTypeUpdates,
      elementUpdates,
    });
    if (!startResponse.isSuccess || !startResponse.operationId) {
      return { success: false, error: startResponse.error || 'Quick Fill apply operation did not start' };
    }

    const operationResult = await pollRevitOperation(startResponse.operationId, {
      onProgress: options?.onProgress,
      parseResult: parseQuickFillApplyResultJson,
    });

    if (!operationResult.success || !operationResult.result) {
      return { success: false, error: operationResult.error || 'Quick Fill apply operation failed' };
    }

    const result = operationResult.result;
    if (result.success) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (result.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`StartQuickFillApply: ${result.error}`);
    }

    return result;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'StartQuickFillApply gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`StartQuickFillApply: ${message}`);
    return { success: false, error: message };
  }
}

function parseQuickFillApplyResultJson(resultJson: string): {
  success: boolean;
  familyTypeResult?: {
    success: boolean;
    totalUpdates?: number;
    successCount?: number;
    errorCount?: number;
    errors?: string[];
    error?: string;
  };
  elementResult?: {
    success: boolean;
    totalUpdates?: number;
    successCount?: number;
    errorCount?: number;
    errors?: string[];
    error?: string;
  };
  error?: string;
} {
  const parsed = JSON.parse(resultJson);
  const familyTypeResult = mapBatchUpdateResult(parsed.familyTypeResult);
  const elementResult = mapBatchUpdateResult(parsed.elementResult);
  const success = (familyTypeResult?.success ?? true) && (elementResult?.success ?? true);
  const error = [familyTypeResult?.error, elementResult?.error].filter(Boolean).join('; ') || undefined;
  return {
    success,
    familyTypeResult,
    elementResult,
    error,
  };
}

function mapBatchUpdateResult(result: any): {
  success: boolean;
  totalUpdates?: number;
  successCount?: number;
  errorCount?: number;
  errors?: string[];
  error?: string;
} | undefined {
  if (!result) {
    return undefined;
  }

  return {
    success: Boolean(result.isSuccess),
    totalUpdates: result.totalUpdates || undefined,
    successCount: result.successCount || undefined,
    errorCount: result.errorCount || undefined,
    errors: Array.isArray(result.errors) && result.errors.length > 0 ? result.errors : undefined,
    error: result.error || undefined,
  };
}

function parseBatchUpdateResultJson(resultJson: string): {
  success: boolean;
  totalUpdates?: number;
  successCount?: number;
  errorCount?: number;
  errors?: string[];
  error?: string;
} {
  return mapBatchUpdateResult(JSON.parse(resultJson)) ?? {
    success: false,
    error: 'Batch update operation returned no result',
  };
}

export async function loadFamilyIntoProjectGrpc(
  familyFilePath: string,
  options?: { onProgress?: (progress: RevitOperationProgressSnapshot) => void }
): Promise<{
  success: boolean;
  familyId?: string;
  familyName?: string;
  category?: string;
  typesCount?: number;
  message?: string;
  error?: string;
}> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('LoadFamilyIntoProject: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    if (options?.onProgress) {
      try {
        const startResponse = await client.startLoadFamilyIntoProject({ familyFilePath });
        if (startResponse.isSuccess && startResponse.operationId) {
          const operationResult = await pollRevitOperation(startResponse.operationId, {
            onProgress: options.onProgress,
            parseResult: parseLoadFamilyIntoProjectResultJson,
          });
          if (!operationResult.success || !operationResult.result) {
            const error = operationResult.error || 'LoadFamilyIntoProject operation failed';
            useIntegrationDiagnosticsStore.getState().setLastGrpcError(`LoadFamilyIntoProject: ${error}`);
            return { success: false, error };
          }

          if (operationResult.result.success) {
            useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
          } else if (operationResult.result.error) {
            useIntegrationDiagnosticsStore
              .getState()
              .setLastGrpcError(`LoadFamilyIntoProject: ${operationResult.result.error}`);
          }
          return operationResult.result;
        }
      } catch (startError) {
        const message = startError instanceof Error ? startError.message : 'StartLoadFamilyIntoProject gRPC error';
        webLog.warn('familyClient', `loadFamilyIntoProject: async start unavailable (${message}); falling back to unary`);
      }
    }

    const response = await client.loadFamilyIntoProject({ familyFilePath });
    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (response.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`LoadFamilyIntoProject: ${response.error}`);
    }
    return {
      success: response.isSuccess,
      familyId: response.familyId || undefined,
      familyName: response.familyName || undefined,
      category: response.category || undefined,
      typesCount: response.typesCount || undefined,
      message: response.message || undefined,
      error: response.error || undefined,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'LoadFamilyIntoProject gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`LoadFamilyIntoProject: ${message}`);
    return { success: false, error: message };
  }
}

/**
 * Re-attaches to a family parameter operation that is still running on the
 * backend after a page reload or WebView2 renderer crash. The backend keeps
 * the operation alive for minutes after completion, so polling simply resumes.
 */
export async function resumeAddParametersToSpecificFamilies(
  operationId: string,
  options: {
    onProgress?: (progress: RevitOperationProgressSnapshot) => void;
  }
): Promise<{
  success: boolean;
  result?: ReturnType<typeof parseAddParametersToSpecificFamiliesResultJson>;
  error?: string;
  lost?: boolean;
}> {
  const probe = await getOperationProgressGrpc(operationId);
  if (!probe.success || !probe.progress) {
    clearPendingRevitOperation(operationId);
    return { success: false, error: probe.error || 'Pending operation is no longer available', lost: true };
  }

  const operationResult = await pollRevitOperation(operationId, {
    onProgress: options.onProgress,
    parseResult: parseAddParametersToSpecificFamiliesResultJson,
  });
  clearPendingRevitOperation(operationId);
  if (!operationResult.result) {
    return { success: false, error: operationResult.error || 'AddParametersToSpecificFamilies operation failed' };
  }

  return { success: true, result: operationResult.result };
}
export async function addParametersToSpecificFamiliesGrpc(request: {
  familyIds: string[];
  parameterNames: string[];
  includeSystemFamilies: boolean;
  categories?: string[];
  sharedParameterFilePath?: string;
  sharedParameterFileContent?: string;
  bindingKind?: string;
  targetObjectTypes?: string[]; objectTypeParameterName?: string;
  requireReversibleMutations?: boolean; expectedDocumentName?: string;
  expectedDocumentPath?: string;
  parameterTypeHints?: Array<{ name: string; dataType: string }>;
  pendingOperationRecovery?: PendingRevitOperationRecoveryMetadata;
  onProgress?: (progress: RevitOperationProgressSnapshot) => void;
}): Promise<{
  success: boolean; modifiedFamiliesCount?: number;
  systemFamilyParamsAdded?: number;
  resolvedFamilyTargetCount?: number;
  processedFamilyTargetCount?: number;
  openedFamilyCount?: number;
  unmodifiedFamilyTargetCount?: number;
  existingFamilyParameterCount?: number; conflictCount?: number; failureCount?: number;
  undoEntries?: ConnectorParameterUndoEntry[]; projectUndoEntries?: ConnectorProjectParameterUndoEntry[];
  modifiedFamilies?: Array<{
    familyName: string;
    familyId: string;
    category: string;
    addedParameters: string[];
  }>;
  message?: string;
  error?: string;
}> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('AddParametersToSpecificFamilies: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }
  let startedOperationId: string | null = null;
  // Record every family mutation reaching the gRPC boundary: route evidence
  // must show family calls even when a caller omitted connector provenance.
  const evidenceDispatchIndex = recordConnectorLiveEvidenceDispatch({
    destination: 'family',
    bindingKind: request.bindingKind ?? '',
    categories: [...(request.categories ?? [])],
    expectedDocumentName: request.expectedDocumentName ?? null,
    expectedDocumentPath: request.expectedDocumentPath ?? null,
    operationId: null,
  });
  try {
    if (request.onProgress) {
      const startResponse = await client.startAddParametersToSpecificFamilies({
        familyIds: request.familyIds,
        parameterNames: request.parameterNames,
        includeSystemFamilies: request.includeSystemFamilies,
        categories: request.categories ?? [],
        sharedParameterFilePath: request.sharedParameterFilePath ?? '',
        sharedParameterFileContent: request.sharedParameterFileContent ?? '',
        bindingKind: request.bindingKind ?? '',
        targetObjectTypes: request.targetObjectTypes ?? [],
        objectTypeParameterName: request.objectTypeParameterName ?? '', requireReversibleMutations: request.requireReversibleMutations ?? false,
        expectedDocumentName: request.expectedDocumentName ?? '',
        expectedDocumentPath: request.expectedDocumentPath ?? '',
        parameterTypeHints: (request.parameterTypeHints ?? []).map((hint) => ({
          name: hint.name,
          dataType: hint.dataType,
        })),
      });
      if (startResponse.isSuccess && startResponse.operationId) {
        startedOperationId = startResponse.operationId;
        recordConnectorLiveEvidenceDispatchOperationId(
          evidenceDispatchIndex,
          startedOperationId,
        );
        // Persist a recovery record only when the caller supplied provenance:
        // this generic client must not invent an owner or title for the work.
        if (request.pendingOperationRecovery) {
          setPendingRevitOperation({
            ...request.pendingOperationRecovery,
            version: 2,
            operationId: startedOperationId,
            operationType: 'parameters.family.add',
            startedAt: Date.now(),
          });
        }
        const operationResult = await pollRevitOperation(startResponse.operationId, {
          onProgress: request.onProgress,
          parseResult: parseAddParametersToSpecificFamiliesResultJson,
        });

        clearPendingRevitOperation(startedOperationId);
        if (!operationResult.result) {
          const error = operationResult.error || 'AddParametersToSpecificFamilies operation failed';
          useIntegrationDiagnosticsStore.getState().setLastGrpcError(`AddParametersToSpecificFamilies: ${error}`);
          return { success: false, error };
        }

        if (operationResult.result.success) {
          useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
        } else if (operationResult.result.error) {
          useIntegrationDiagnosticsStore
            .getState()
            .setLastGrpcError(`AddParametersToSpecificFamilies: ${operationResult.result.error}`);
        }

        return operationResult.result;
      }

      return {
        success: false,
        error: startResponse.error || 'AddParametersToSpecificFamilies did not start a progress operation',
      };
    }

    const response = await client.addParametersToSpecificFamilies({
      familyIds: request.familyIds,
      parameterNames: request.parameterNames,
      includeSystemFamilies: request.includeSystemFamilies,
      categories: request.categories ?? [],
      sharedParameterFilePath: request.sharedParameterFilePath ?? '',
      sharedParameterFileContent: request.sharedParameterFileContent ?? '',
      bindingKind: request.bindingKind ?? '',
      targetObjectTypes: request.targetObjectTypes ?? [],
      objectTypeParameterName: request.objectTypeParameterName ?? '', requireReversibleMutations: request.requireReversibleMutations ?? false,
      expectedDocumentName: request.expectedDocumentName ?? '',
      expectedDocumentPath: request.expectedDocumentPath ?? '',
      parameterTypeHints: (request.parameterTypeHints ?? []).map((hint) => ({
        name: hint.name,
        dataType: hint.dataType,
      })),
    });
    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (response.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`AddParametersToSpecificFamilies: ${response.error}`);
    }
    return {
      success: response.isSuccess,
      modifiedFamiliesCount: response.modifiedFamiliesCount || undefined,
      systemFamilyParamsAdded: response.systemFamilyParamsAdded || undefined,
      resolvedFamilyTargetCount: response.resolvedFamilyTargetCount || undefined,
      processedFamilyTargetCount: response.processedFamilyTargetCount || undefined,
      openedFamilyCount: response.openedFamilyCount || undefined,
      unmodifiedFamilyTargetCount: response.unmodifiedFamilyTargetCount || undefined,
      existingFamilyParameterCount: response.existingFamilyParameterCount || undefined,
      conflictCount: response.conflictCount || undefined,
      failureCount: response.failureCount || undefined,
      undoEntries: mapFamilyUndoEntries(response.undoEntries), projectUndoEntries: mapProjectUndoEntries(response.projectUndoEntries),
      modifiedFamilies: response.modifiedFamilies.map(mf => ({
        familyName: mf.familyName,
        familyId: mf.familyId,
        category: mf.category,
        addedParameters: mf.addedParameters,
      })),
      message: response.message || undefined,
      error: response.error || undefined,
    };
  } catch (error) {
    clearPendingRevitOperation(startedOperationId ?? undefined);
    const message = error instanceof Error ? error.message : 'AddParametersToSpecificFamilies gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`AddParametersToSpecificFamilies: ${message}`);
    return { success: false, error: message };
  }
}

function parseLoadFamilyIntoProjectResultJson(resultJson: string): {
  success: boolean;
  familyId?: string;
  familyName?: string;
  category?: string;
  typesCount?: number;
  message?: string;
  error?: string;
} {
  const parsed = JSON.parse(resultJson);
  return {
    success: Boolean(parsed.isSuccess),
    familyId: parsed.familyId || undefined,
    familyName: parsed.familyName || undefined,
    category: parsed.category || undefined,
    typesCount: parsed.typesCount || undefined,
    message: parsed.message || undefined,
    error: parsed.error || undefined,
  };
}

function parseAddParametersToSpecificFamiliesResultJson(resultJson: string): {
  success: boolean;
  modifiedFamiliesCount?: number;
  systemFamilyParamsAdded?: number;
  resolvedFamilyTargetCount?: number;
  processedFamilyTargetCount?: number;
  openedFamilyCount?: number;
  unmodifiedFamilyTargetCount?: number;
  existingFamilyParameterCount?: number; conflictCount?: number; failureCount?: number;
  undoEntries?: ConnectorParameterUndoEntry[]; projectUndoEntries?: ConnectorProjectParameterUndoEntry[];
  modifiedFamilies?: Array<{
    familyName: string;
    familyId: string;
    category: string;
    addedParameters: string[];
  }>;
  message?: string;
  error?: string;
} {
  const parsed = JSON.parse(resultJson);
  return {
    success: Boolean(parsed.isSuccess),
    modifiedFamiliesCount: parsed.modifiedFamiliesCount || undefined,
    systemFamilyParamsAdded: parsed.systemFamilyParamsAdded || undefined,
    resolvedFamilyTargetCount: parsed.resolvedFamilyTargetCount || undefined,
    processedFamilyTargetCount: parsed.processedFamilyTargetCount || undefined,
    openedFamilyCount: parsed.openedFamilyCount || undefined,
    unmodifiedFamilyTargetCount: parsed.unmodifiedFamilyTargetCount || undefined,
    existingFamilyParameterCount: parsed.existingFamilyParameterCount || undefined,
    conflictCount: parsed.conflictCount || undefined,
    failureCount: parsed.failureCount || undefined,
    undoEntries: mapFamilyUndoEntries(parsed.undoEntries), projectUndoEntries: mapProjectUndoEntries(parsed.projectUndoEntries),
    modifiedFamilies: Array.isArray(parsed.modifiedFamilies)
      ? parsed.modifiedFamilies.map((mf: any) => ({
          familyName: mf.familyName ?? '',
          familyId: mf.familyId ?? '',
          category: mf.category ?? '',
          addedParameters: Array.isArray(mf.addedParameters) ? mf.addedParameters : [],
        }))
      : [],
    message: parsed.message || undefined,
    error: parsed.error || undefined,
  };
}

export async function undoAddedParametersToSpecificFamiliesGrpc(request: {
  entries: Array<{
    familyId: string;
    familyName?: string;
    parameterName: string;
    existedBefore: boolean;
    previousBindingKind?: string;
  }>;
  expectedDocumentName?: string;
  expectedDocumentPath?: string;
  onProgress?: (progress: RevitOperationProgressSnapshot) => void;
}): Promise<{
  success: boolean;
  undoneFamiliesCount?: number;
  removedParameterCount?: number;
  restoredParameterCount?: number;
  errors?: string[];
  message?: string;
  error?: string;
}> {
  const client = getClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('UndoAddedParametersToSpecificFamilies: gRPC transport is unavailable');
    return { success: false, error: 'gRPC transport is unavailable' };
  }

  try {
    const undoRequest = {
      entries: request.entries.map((entry) => ({
        familyId: entry.familyId,
        familyName: entry.familyName ?? '',
        parameterName: entry.parameterName,
        existedBefore: entry.existedBefore,
        previousBindingKind: entry.previousBindingKind ?? '',
      })),
      expectedDocumentName: request.expectedDocumentName ?? '',
      expectedDocumentPath: request.expectedDocumentPath ?? '',
    };

    try {
      const startResponse = await client.startUndoAddedParametersToSpecificFamilies(undoRequest);
      if (startResponse.isSuccess && startResponse.operationId) {
        const operationResult = await pollRevitOperation(startResponse.operationId, {
          onProgress: request.onProgress,
          parseResult: parseUndoAddedParametersToSpecificFamiliesResultJson,
        });

        if (!operationResult.success || !operationResult.result) {
          const error = operationResult.error || 'UndoAddedParametersToSpecificFamilies operation failed';
          useIntegrationDiagnosticsStore.getState().setLastGrpcError(`UndoAddedParametersToSpecificFamilies: ${error}`);
          return { success: false, error };
        }

        if (operationResult.result.success) {
          useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
        } else if (operationResult.result.error) {
          useIntegrationDiagnosticsStore
            .getState()
            .setLastGrpcError(`UndoAddedParametersToSpecificFamilies: ${operationResult.result.error}`);
        }

        return operationResult.result;
      }
    } catch (startError) {
      const message = startError instanceof Error ? startError.message : 'StartUndoAddedParametersToSpecificFamilies gRPC error';
      webLog.warn('familyClient', `undoAddedParametersToSpecificFamilies: async start unavailable (${message}); falling back to unary`);
    }

    const response = await client.undoAddedParametersToSpecificFamilies(undoRequest);

    if (response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);
    } else if (response.error) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`UndoAddedParametersToSpecificFamilies: ${response.error}`);
    }

    return {
      success: response.isSuccess,
      undoneFamiliesCount: response.undoneFamiliesCount || undefined,
      removedParameterCount: response.removedParameterCount || undefined,
      restoredParameterCount: response.restoredParameterCount || undefined,
      errors: response.errors.length > 0 ? response.errors : undefined,
      message: response.message || undefined,
      error: response.error || undefined,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'UndoAddedParametersToSpecificFamilies gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`UndoAddedParametersToSpecificFamilies: ${message}`);
    return { success: false, error: message };
  }
}

function parseUndoAddedParametersToSpecificFamiliesResultJson(resultJson: string): {
  success: boolean;
  undoneFamiliesCount?: number;
  removedParameterCount?: number;
  restoredParameterCount?: number;
  errors?: string[];
  message?: string;
  error?: string;
} {
  const parsed = JSON.parse(resultJson);
  return {
    success: Boolean(parsed.isSuccess),
    undoneFamiliesCount: parsed.undoneFamiliesCount || undefined,
    removedParameterCount: parsed.removedParameterCount || undefined,
    restoredParameterCount: parsed.restoredParameterCount || undefined,
    errors: Array.isArray(parsed.errors) && parsed.errors.length > 0 ? parsed.errors : undefined,
    message: parsed.message || undefined,
    error: parsed.error || undefined,
  };
}
